import type { ObjectResponse } from '../../../common/types';
import { annotationToolApiV1, getAnnotationToolBasePath } from '../../../lib';
import { AddSpanRequest, Annotation } from '../../workspace-common/types/responseTypes';
import { SUPERVISOR_TAG } from '../constants';
import { CompleteSupervisionRequest } from '../types/request';
import type {
  DeleteAnnotationResponse,
  SupervisorAddFinalizedAnnotationRequest,
  SupervisorCreateAnnotationRequest,
  SupervisorRejectAnnotationResponse,
  SupervisorWorkspaceResponse,
  UpdateSupervisorAttributesRequest,
} from '../types/response';
export const supervisorApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [SUPERVISOR_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getSupervisorWorkspace: builder.query<
        ObjectResponse<SupervisorWorkspaceResponse>,
        {
          encounterId: string;
          batchId: string;
        }
      >({
        query: ({ encounterId, batchId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace`;
        },
        providesTags: [SUPERVISOR_TAG],
      }),

      resolveSupervisorConflict: builder.mutation<
        ObjectResponse<{ status: string }>,
        {
          encounterId: string;
          batchId: string;
          annotationIdToAccept: string;
          annotationIdToReject?: string;
        }
      >({
        query: ({ encounterId, batchId, annotationIdToAccept, annotationIdToReject = '' }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace/resolve`,
            method: 'POST',
            body: {
              annotationIdToAccept,
              annotationIdToReject,
            },
          };
        },
        invalidatesTags: [SUPERVISOR_TAG],
      }),

      rejectConflictAnnotation: builder.mutation<
        ObjectResponse<{ status: string }>,
        {
          encounterId: string;
          batchId: string;
          annotationIdToReject: string;
        }
      >({
        query: ({ encounterId, batchId, annotationIdToReject }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace/reject`,
            method: 'POST',
            body: {
              annotationIdToReject,
            },
          };
        },
        invalidatesTags: [SUPERVISOR_TAG],
      }),
      updateSuperviseAttributes: builder.mutation<
        ObjectResponse<Annotation>,
        UpdateSupervisorAttributesRequest
      >({
        query: ({ annotationId, attributes, spans, evidenceIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}/attributes`,
            method: 'PUT',
            body: {
              attributes,
              spans: spans || [],
              ...(evidenceIdsToDelete?.length ? { evidenceIdsToDelete } : {}),
            },
          };
        },
        invalidatesTags: [SUPERVISOR_TAG],
      }),
      supervisorRejectAndCreateAnnotation: builder.mutation<
        ObjectResponse<SupervisorRejectAnnotationResponse>,
        SupervisorCreateAnnotationRequest
      >({
        query: ({
          encounterId,
          conflictId,
          annotationIdsToReject,
          conceptId,
          attributes,
          spans,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations`,
            method: 'POST',
            body: {
              conceptId,
              conflictId,
              annotationIdsToReject,
              attributes,
              spans: spans || [],
            },
          };
        },
        // Mirror supervisorAddFinalizedAnnotation: skip invalidation on error to
        // keep the in-progress form mounted with user-entered values.
        invalidatesTags: (result, error) => (error ? [] : [SUPERVISOR_TAG]),
      }),
      deleteResolvedAnnotation: builder.mutation<ObjectResponse<DeleteAnnotationResponse>, string>({
        query: (annotationId) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}`,
            method: 'DELETE',
          };
        },

        invalidatesTags: [SUPERVISOR_TAG],
      }),
      addResolvedAnnotationSpans: builder.mutation<
        ObjectResponse<{ annotationId: string; added: boolean }>,
        AddSpanRequest
      >({
        query: ({ annotationId, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}/spans`,
            method: 'POST',
            body: {
              spans,
            },
          };
        },

        invalidatesTags: [SUPERVISOR_TAG],
      }),
      supervisorAddFinalizedAnnotation: builder.mutation<
        ObjectResponse<Annotation>,
        SupervisorAddFinalizedAnnotationRequest
      >({
        query: ({ encounterId, batchId, conceptId, attributes, spans, annotationIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/supervisor/annotations/create`,
            method: 'POST',
            body: {
              encounterId,
              batchId,
              conceptId,
              attributes,
              spans: spans || [],
              ...(annotationIdsToDelete?.length ? { annotationIdsToDelete } : {}),
            },
          };
        },
        // RTK Query 2.x fires static invalidatesTags on rejectedWithValue too, which would
        // refetch the workspace and unmount the in-progress form, wiping user-entered values.
        invalidatesTags: (result, error) => (error ? [] : [SUPERVISOR_TAG]),
      }),
      markSupervisionComplete: builder.mutation<
        ObjectResponse<CompleteSupervisionRequest>,
        CompleteSupervisionRequest
      >({
        query: ({ encounterId, batchId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/tasks/${batchId}/${encounterId}/mark-complete`,
            method: 'POST',
          };
        },

        invalidatesTags: [SUPERVISOR_TAG],
      }),
    }),
  });

export const {
  useGetSupervisorWorkspaceQuery,
  useResolveSupervisorConflictMutation,
  useUpdateSuperviseAttributesMutation,
  useSupervisorRejectAndCreateAnnotationMutation,
  useDeleteResolvedAnnotationMutation,
  useAddResolvedAnnotationSpansMutation,
  useRejectConflictAnnotationMutation,
  useMarkSupervisionCompleteMutation,
  useSupervisorAddFinalizedAnnotationMutation,
} = supervisorApiSlice;
